import * as React from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  SxProps,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

export type DetailItem = {
  key: string
  label: React.ReactNode
  value?: React.ReactNode
  hint?: string
  copyable?: boolean | ((valText: string) => string)
  href?: string
  onClick?: () => void
  sx?: SxProps
}

export type ReusableProfileInfoCardProps = {
  title?: React.ReactNode
  online?: boolean
  titleBadge?: React.ReactNode
  items: DetailItem[]
  footerNote?: React.ReactNode
  elevation?: number
  width?: number | string
  maxHeight?: number | string
  dense?: boolean
  sx?: SxProps
  icon?: any
}

function getTextFromNode(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getTextFromNode).join(' ')
  return ''
}

const labelSx: SxProps = {
  color: '#8F98A5',
  fontSize: 12,
  whiteSpace: 'nowrap',
  ml: '10px',
  fontFamily: 'Rubik, sans-serif',
}

const valueSx: SxProps = {
  color: '#003F61',
  fontSize: 12,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: 'Rubik, sans-serif',
}

export default function ReusableInfoCard({
  title = 'פרטי חיישן',
  titleBadge,
  items,
  footerNote,
  elevation = 0,
  maxHeight,
  dense,
  sx,
  icon,
}: ReusableProfileInfoCardProps) {
  const spacing = dense ? 1 : '-7px'

  return (
    <Paper
      elevation={elevation}
      sx={{
        direction: 'rtl',
        p: 1,
        borderRadius: 1,
        overflow: maxHeight ? 'auto' : 'auto',
        height: 'auto',
        border: '1px solid #323B771A',
        width: '250px',
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon && <img src={icon} alt={icon} />}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 500, fontFamily: 'Rubik, sans-serif' }}
        >
          {title}
        </Typography>
        {titleBadge && (
          <Chip
            size="small"
            variant="outlined"
            label={titleBadge}
            sx={{ height: 24 }}
          />
        )}
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Stack spacing={spacing}>
        {items.map(item => {
          const valText = getTextFromNode(item.value)

          const handleCopy = async () => {
            try {
              const copyPayload =
                typeof item.copyable === 'function'
                  ? item.copyable(valText)
                  : valText
              await navigator.clipboard.writeText(copyPayload)
            } catch (err) {
              // no‑op
            }
          }

          const Row = (
            <Stack
              key={item.key}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                minHeight: 32,
                px: 0.5,
                ...(item.onClick && { cursor: 'pointer' }),
                ...item.sx,
              }}
              onClick={item.onClick}
            >
              {/* Label (right) */}
              <Box sx={{ textAlign: 'right' }}>
                <Typography component="div" sx={labelSx}>
                  {item.label}
                </Typography>
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ flex: 1, justifyContent: 'flex-start', minWidth: 0 }}
              >
                <Tooltip
                  title={item.hint ?? ''}
                  disableHoverListener={!item.hint}
                >
                  <Typography
                    component="div"
                    sx={{ ...valueSx, minWidth: 0 }}
                    noWrap
                  >
                    {item.value ?? '-'}
                  </Typography>
                </Tooltip>

                {item.copyable && valText && (
                  <Tooltip title="העתק">
                    <IconButton size="small" onClick={handleCopy}>
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
                {item.href && (
                  <Tooltip title="פתח">
                    <IconButton
                      size="small"
                      component="a"
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          )

          return <React.Fragment key={item.key}>{Row}</React.Fragment>
        })}
      </Stack>

      {footerNote && (
        <>
          <Divider />
          <Box>
            <Typography
              variant="caption"
              sx={{ color: '#8F98A5', fontFamily: 'Rubik, sans-serif' }}
            >
              הערה תפעולית
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#003F61',
                fontSize: 13,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              {footerNote}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  )
}
